import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { LoginPage } from './pages/LoginPage';
import { FloorPage } from './pages/FloorPage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { SessionsPage } from './pages/SessionsPage';
import { StationsAdminPage } from './pages/StationsAdminPage';
import { StaffAdminPage } from './pages/StaffAdminPage';
import { RechargeAuditPage } from './pages/RechargeAuditPage';
import { MenuAdminPage } from './pages/MenuAdminPage';
import { RechargePacksAdminPage } from './pages/RechargePacksAdminPage';
import { ReportsPage } from './pages/ReportsPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { StaffTabPage } from './pages/StaffTabPage';

// Admin-only screens still render behind this gate, but the backend is the
// real boundary — a staff user hitting these routes directly gets a 403.
function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/floor" replace />;
}

export function App() {
  const { staff, restoring } = useAuth();

  if (restoring) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <Spinner label="Restoring your session…" />
      </div>
    );
  }

  if (!staff) return <LoginPage />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/floor" element={<FloorPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:customerId" element={<CustomerDetailPage />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/staff-tab" element={<StaffTabPage />} />
        <Route
          path="/admin/reports"
          element={
            <RequireAdmin>
              <ReportsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/stations"
          element={
            <RequireAdmin>
              <StationsAdminPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/recharge-packs"
          element={
            <RequireAdmin>
              <RechargePacksAdminPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/menu"
          element={
            <RequireAdmin>
              <MenuAdminPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/staff"
          element={
            <RequireAdmin>
              <StaffAdminPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/recharge-edits"
          element={
            <RequireAdmin>
              <RechargeAuditPage />
            </RequireAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/floor" replace />} />
      </Route>
    </Routes>
  );
}
